import React, { useEffect, useMemo, useState } from 'react';
import { CgSpinner } from 'react-icons/cg';
import { FaCheck, FaChevronLeft, FaChevronRight, FaUserPlus } from 'react-icons/fa';

import { getSysManagerGroups } from './sysManagerGroupsApi.js';
import { createSysManagerStaff } from './sysManagerStaffApi.js';

const STEPS = ['Staff details', 'Account setup', 'Review'];
const USER_TYPES = ['Staff', 'Governor', 'Contractor', 'Supply'];

/** Guided account-creation modal for System Manager Staff. */
export default function SysManagerAddStaff({ onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState({ displayName: '', email: '', userType: 'Staff', groupId: '' });
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const selectedGroupName = useMemo(
    () => groups.find((group) => group.groupId === draft.groupId)?.name || 'No group',
    [draft.groupId, groups],
  );
  const detailValid = draft.displayName.trim().length >= 2
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim());

  useEffect(() => {
    let active = true;

    /** Load the current school's groups for the account setup selector. */
    async function loadGroups() {
      try {
        const response = await getSysManagerGroups();
        if (active) setGroups(response.groups);
      } catch (error) {
        console.error('Failed to load permission groups', error);
        if (active) setErrorMessage('Permission groups could not be loaded.');
      } finally {
        if (active) setGroupsLoading(false);
      }
    }

    loadGroups();
    return () => { active = false; };
  }, []);

  /** Submit the account details, selected group, and allow-listed user type. */
  async function createStaff() {
    if (saving) return;
    setSaving(true);
    setErrorMessage('');
    try {
      const response = await createSysManagerStaff({
        ...draft,
        displayName: draft.displayName.trim(),
        email: draft.email.trim().toLowerCase(),
      });
      onCreated(response.staff);
    } catch (error) {
      console.error('Failed to create staff account', error);
      setErrorMessage(error?.message || 'The staff account could not be created.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className='sysManager_staff_modalBackdrop' role='presentation'>
      <div className='sysManager_addStaff' role='dialog' aria-modal='true' aria-labelledby='add-staff-title'>
        <button className='sysManager_staff_modalClose' type='button' aria-label='Close add staff' disabled={saving} onClick={onClose}>×</button>
        <div className='sysManager_addStaff_heading'>
          <span><FaUserPlus aria-hidden='true' /></span>
          <div><h2 id='add-staff-title'>Add new staff</h2><p>Step {step + 1} of {STEPS.length}: {STEPS[step]}</p></div>
        </div>
        <ol className='sysManager_addStaff_steps' aria-label='Account creation progress'>
          {STEPS.map((label, index) => <li className={index === step ? 'active' : index < step ? 'complete' : ''} key={label}><span>{index < step ? <FaCheck /> : index + 1}</span><small>{label}</small></li>)}
        </ol>

        <div className='sysManager_addStaff_body'>
          {step === 0 && <div className='sysManager_addStaff_fields'>
            <label><span>Display name</span><input value={draft.displayName} maxLength={120} autoFocus onChange={(event) => setDraft({ ...draft, displayName: event.target.value })} /></label>
            <label><span>Email address</span><input type='email' value={draft.email} maxLength={254} onChange={(event) => setDraft({ ...draft, email: event.target.value })} /></label>
            <p>The new user will receive an email with an invitation and temporary password.</p>
          </div>}

          {step === 1 && <div className='sysManager_addStaff_fields'>
            <p className='sysManager_addStaff_intro'>Choose the user type and permission group for this account. Access is managed through the selected group after creation.</p>
            <label><span>User type</span><select value={draft.userType} onChange={(event) => setDraft({ ...draft, userType: event.target.value })}>{USER_TYPES.map((userType) => <option key={userType} value={userType}>{userType}</option>)}</select></label>
            <label><span>Permission group</span><select value={draft.groupId} disabled={groupsLoading} onChange={(event) => setDraft({ ...draft, groupId: event.target.value })}><option value=''>{groupsLoading ? 'Loading groups…' : 'No group'}</option>{groups.map((group) => <option key={group.groupId} value={group.groupId}>{group.name}</option>)}</select></label>
            {!groupsLoading && groups.length === 0 && <p>No permission groups have been configured. The account can still be created with no group.</p>}
          </div>}

          {step === 2 && <div className='sysManager_addStaff_review'>
            <section><h3>Staff details</h3><p><strong>{draft.displayName.trim()}</strong><br />{draft.email.trim().toLowerCase()}</p></section>
            <section><h3>User type</h3><p>{draft.userType}</p></section>
            <section><h3>Permission group</h3><p>{selectedGroupName}</p></section>
            <p className='sysManager_addStaff_notice'>Creating the account sends the invitation email immediately.</p>
          </div>}
          {errorMessage && <div className='sysManager_addStaff_error' role='alert'>{errorMessage}</div>}
        </div>

        <div className='sysManager_addStaff_actions'>
          <button type='button' className='sysManager_staff_modalSecondary' disabled={saving || step === 0} onClick={() => { setErrorMessage(''); setStep(step - 1); }}><FaChevronLeft /> Back</button>
          {step < STEPS.length - 1
            ? <button type='button' className='sysManager_staff_modalPrimary' disabled={(step === 0 && !detailValid) || (step === 1 && groupsLoading)} onClick={() => { setErrorMessage(''); setStep(step + 1); }}>Continue <FaChevronRight /></button>
            : <button type='button' className='sysManager_staff_modalPrimary' disabled={saving} onClick={createStaff}>{saving ? <><CgSpinner className='sysManager_staff_buttonSpinner' /> Creating</> : 'Create staff account'}</button>}
        </div>
      </div>
    </div>
  );
}
